import React, { Fragment, Suspense, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import { useLazyChildRenderer } from "hooks/useLazyChildRenderer";
import { ReactComponent as Caret } from "assets/icons/caret.svg";

import "./Dropdown.scss";

const DropdownOptions = React.lazy(() => import(/* webpackChunkName: "dropdown" */ './DropdownOptions')
  .then(module => ({ default: module.DropdownOptions })));

export const Dropdown = props => {
  const [isOpen, setOpen] = useState(false);
  const shouldRenderContent = useLazyChildRenderer(isOpen);
  const dropdownNode = useRef(null);
  const contentNode = useRef(null);
 
  const toggle = () => !props.isDisabled && setOpen(!isOpen);

  const handleClick = e => {
    if (isOpen) {
      if (!contentNode.current.contains(e.target) && !dropdownNode.current.contains(e.target)) {
        toggle();
      }
    }
  };

  useEffect(() => {
    if (isOpen) {     
      document.addEventListener("mousedown", handleClick);
    }
    return () => {
      if (isOpen) {
        document.removeEventListener("mousedown", handleClick);
      }
    };
  }, [isOpen]);

  const selectionText = !props.isPlain && props.selectionText;
  const isActive = selectionText;

  const className = cx(
    "dropdown",
    props.className,
    { "dropdown--multi-column": props.multiColumn },
    { "dropdown--plain": props.isPlain },
    { "is-disabled": props.isDisabled },
    { "is-open": isOpen },
    { "is-active": isActive && !props.isDisabled },
  );

  const shouldRenderOptions = shouldRenderContent && !props.isDisabled;

  return (
    <div className={className} ref={dropdownNode}>
      <div className="dropdown__trigger" onClick={toggle}>
        <div className="dropdown__label">
          {selectionText ? (
            <Fragment>
              <span className="dropdown__placeholder">{props.label}</span>
              <span className="dropdown__selection">{selectionText}</span>
            </Fragment>
          ) : (
            props.label
          )}
        </div>
        <div className="dropdown__icon">
          <Caret />
        </div>
      </div>
      {shouldRenderOptions && (
        <div className={props.contentClassName} ref={contentNode}>
          {props.options ? 
         <Suspense fallback={""}>
            <DropdownOptions                   
              {...props}         
              isOpen={isOpen}
              onChange={option => {
                props.onChange(option);
                setOpen(false);
              }}
            />
          </Suspense> : props.children}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
    }),
  ),
  selectedValue: PropTypes.any,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  multiColumn: PropTypes.bool,
  contentClassName: PropTypes.string.isRequired,
  useCarousel: PropTypes.bool.isRequired,
};

Dropdown.defaultProps = {
  isDisabled: false,
  multiColumn: false,
  options: null,
  contentClassName: "dropdown__content",
  useCarousel: false,
};
